import { computed, ref, watch } from 'vue';
import { usePB } from '@/composables/database/use-pocket-base.ts';
import { isDirectBusinessAccount } from '@/utils/account-capabilities.ts';
import { readVersionedJson, removeStoredValue, writeVersionedJson } from '@/utils/browser-storage.ts';

export type AccountViewMode = 'actual' | 'personal' | 'business';
export type EffectiveAccountType = Exclude<AccountViewMode, 'actual'>;

const STORAGE_KEY = 'vennevent:admin-account-view-mode';
const selectedMode = ref<AccountViewMode>('actual');
let initialized = false;
let activeAdminId = '';
type StoredAccountPreview = { adminId: string; mode: 'personal' | 'business' };

function storageOptions() {
  return {
    key: STORAGE_KEY,
    version: 1,
    storage: () => window.sessionStorage,
    maxBytes: 256,
    validate(value: unknown): value is StoredAccountPreview {
      if (!value || typeof value !== 'object') return false;
      const candidate = value as Partial<StoredAccountPreview>;
      return (
        typeof candidate.adminId === 'string' &&
        candidate.adminId.length > 0 &&
        candidate.adminId.length <= 64 &&
        (candidate.mode === 'personal' || candidate.mode === 'business')
      );
    },
    migrateLegacy(value: unknown): StoredAccountPreview | undefined {
      if (!value || typeof value !== 'object') return undefined;
      const candidate = value as Partial<StoredAccountPreview>;
      if (typeof candidate.adminId === 'string' && (candidate.mode === 'personal' || candidate.mode === 'business')) return candidate as StoredAccountPreview;
    },
  };
}

function isAdminAccount(account: unknown): account is { id: string; moderation_role: string } {
  if (!account || typeof account !== 'object') return false;
  const candidate = account as { id?: unknown; moderation_role?: unknown };
  return typeof candidate.id === 'string' && candidate.moderation_role === 'admin';
}

function readStoredMode(adminId: string): AccountViewMode {
  const stored = readVersionedJson(storageOptions());
  if (stored?.adminId === adminId) return stored.mode;
  return 'actual';
}

function clearStoredMode() {
  removeStoredValue(() => window.sessionStorage, STORAGE_KEY);
}

export function useAccountCapabilities() {
  const pb = usePB();

  if (!initialized) {
    initialized = true;
    watch(
      pb.currentUser,
      (account) => {
        if (!isAdminAccount(account)) {
          selectedMode.value = 'actual';
          activeAdminId = '';
          clearStoredMode();
          return;
        }

        if (account.id !== activeAdminId) {
          activeAdminId = account.id;
          selectedMode.value = readStoredMode(account.id);
          if (selectedMode.value === 'actual') clearStoredMode();
        }
      },
      { immediate: true, flush: 'sync' },
    );
  }

  const isAdmin = computed(() => isAdminAccount(pb.currentUser.value));
  const actualAccountType = computed<EffectiveAccountType>(() => (isDirectBusinessAccount(pb.currentUser.value) ? 'business' : 'personal'));
  const effectiveAccountType = computed<EffectiveAccountType>(() =>
    isAdmin.value && selectedMode.value !== 'actual' ? selectedMode.value : actualAccountType.value,
  );
  const isPreviewing = computed(() => isAdmin.value && selectedMode.value !== 'actual');
  const canUsePersonalActivity = computed(() => effectiveAccountType.value === 'personal');
  const canUseBusinessIdentityFeatures = computed(() => effectiveAccountType.value === 'business');

  function setViewMode(mode: AccountViewMode) {
    if (!isAdmin.value || !activeAdminId) return;
    selectedMode.value = mode;
    if (mode === 'actual') {
      clearStoredMode();
    } else {
      writeVersionedJson(storageOptions(), { adminId: activeAdminId, mode });
    }
  }

  const viewMode = computed<AccountViewMode>({
    get: () => (isAdmin.value ? selectedMode.value : 'actual'),
    set: setViewMode,
  });

  return {
    actualAccountType,
    canUseBusinessIdentityFeatures,
    canUsePersonalActivity,
    effectiveAccountType,
    isAdmin,
    isPreviewing,
    setViewMode,
    viewMode,
  };
}
